import { i18nTime, isI18nString, useI18n } from '@affine/i18n';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { type CSSProperties, useLayoutEffect, useRef } from 'react';

import type { QuickSearchItem } from '../types/item';
import { HighlightText } from './highlight-text';
import {
  SearchHitOfflinePill,
  SearchHitTrashPill,
  SearchHitVisual,
} from './search-hit-visual';
import * as styles from './search-results.css';

/**
 * Attributes of a doc result row at `index` in its rendered list: its kind,
 * whether it closes its doc's group (the guide line ends there) and its
 * entrance delay.
 */
export function searchResultRowAttributes(
  items: readonly QuickSearchItem[],
  index: number
) {
  const item = items[index];
  const next = items[index + 1];
  const continuesGroup = next?.row === 'place' || next?.row === 'more';
  return {
    'data-row': item.row,
    'data-has-places':
      item.row === 'doc' && continuesGroup ? 'true' : undefined,
    'data-group-end':
      item.row !== 'doc' && !continuesGroup ? 'true' : undefined,
    'data-trashed': item.trashed ? 'true' : undefined,
    'data-offline': item.offline,
    style: assignInlineVars({
      [styles.rowIndex]: String(index),
    }) as CSSProperties,
  };
}

/**
 * The content of a doc result row, the same in quick and advanced search: the
 * doc (its own icon, the title, the count of its places, the time), a place
 * (the thumb of what was found, where it lies with its hits, the snippet) or
 * "Show N more".
 */
export function SearchResultRowContent({ item }: { item: QuickSearchItem }) {
  const i18n = useI18n();
  const [title, subTitle] = isI18nString(item.label)
    ? [i18n.t(item.label), null]
    : [
        i18n.t(item.label.title),
        item.label.subTitle ? i18n.t(item.label.subTitle) : null,
      ];

  if (item.row === 'more') return <>{title}</>;

  if (item.row === 'doc') {
    return (
      <>
        <span className={styles.docIcon}>
          {item.icon &&
            (typeof item.icon === 'function' ? <item.icon /> : item.icon)}
        </span>
        <span className={styles.docTitle} data-testid="cmdk-label">
          <HighlightText text={title} start="<b>" end="</b>" />
        </span>
        {item.badge ? (
          <span className={styles.docCount}>{i18n.t(item.badge)}</span>
        ) : null}
        {item.trashed ? <SearchHitTrashPill /> : null}
        {item.offline ? <SearchHitOfflinePill state={item.offline} /> : null}
        {item.timestamp ? (
          <span className={styles.docTime}>
            {i18nTime(new Date(item.timestamp))}
          </span>
        ) : null}
      </>
    );
  }

  return (
    <>
      {item.visual ? <SearchHitVisual visual={item.visual} /> : null}
      <span className={styles.placeText} data-testid="cmdk-label">
        <span className={styles.placeLabel}>
          <span className={styles.placeLabelText}>
            <HighlightText text={title} start="<b>" end="</b>" />
          </span>
          {item.badge ? (
            <span className={styles.placeBadge}>{i18n.t(item.badge)}</span>
          ) : null}
        </span>
        {subTitle ? (
          <span className={styles.placeSnippet}>
            <HighlightText text={subTitle} start="<b>" end="</b>" />
          </span>
        ) : null}
      </span>
    </>
  );
}

/** Offset of `element` inside `ancestor`, from layout (entrance transforms aside). */
function layoutOffset(element: HTMLElement, ancestor: Element) {
  let top = 0;
  let left = 0;
  let node: HTMLElement | null = element;
  while (node && node !== ancestor) {
    top += node.offsetTop;
    left += node.offsetLeft;
    node = node.offsetParent as HTMLElement | null;
  }
  return node === ancestor ? { top, left } : null;
}

/**
 * The sliding selection highlight: render it as the first child of the list's
 * positioned content box. It follows the row matching `rowSelector` in that box
 * whose `data-selected` is "true" whenever the selection moves, rows come or go,
 * or the box resizes (e.g. after "Show N more"); it appears in place and only
 * slides between rows. It watches the DOM, not props or a list ref: cmdk marks
 * its selected item and re-attaches its list's ref only after this layout
 * effect has run.
 */
export function SelectionCursor({ rowSelector }: { rowSelector: string }) {
  const cursorRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const cursor = cursorRef.current;
    const box = cursor?.parentElement;
    if (!cursor || !box) return;
    const selected = `${rowSelector}[data-selected="true"]`;
    const place = () => {
      const row = box.querySelector<HTMLElement>(selected);
      const host = cursor.offsetParent;
      const offset = row && host ? layoutOffset(row, host) : null;
      if (!row || !offset) {
        cursor.dataset.visible = 'false';
        return;
      }
      const appearing = cursor.dataset.visible !== 'true';
      if (appearing) cursor.dataset.instant = 'true';
      cursor.style.transform = `translate(${offset.left}px, ${offset.top}px)`;
      cursor.style.width = `${row.offsetWidth}px`;
      cursor.style.height = `${row.offsetHeight}px`;
      cursor.dataset.tone =
        row.dataset.isDanger === 'true' ? 'danger' : 'default';
      cursor.dataset.visible = 'true';
      if (appearing) {
        // Commit the start position before transitions apply again.
        void cursor.offsetWidth;
        delete cursor.dataset.instant;
      }
    };
    const resize = new ResizeObserver(place);
    const observeRows = () => {
      resize.observe(box);
      for (const child of box.children) resize.observe(child);
    };
    const mutation = new MutationObserver(() => {
      observeRows();
      place();
    });
    place();
    observeRows();
    mutation.observe(box, {
      subtree: true,
      childList: true,
      attributeFilter: ['data-selected'],
    });
    return () => {
      mutation.disconnect();
      resize.disconnect();
    };
  }, [rowSelector]);

  return (
    <div
      ref={cursorRef}
      className={styles.selectionCursor}
      data-visible="false"
      aria-hidden="true"
    />
  );
}
